import { HostMatchmakingState } from '@playcastdotio/Messaging';
import { getStateStore } from '../../stores';
import { sendMatchmakerHostConfig } from '../managers/matchmaker';
import { hostState, steamInstallState } from '../../stores/shared';
import { notifyPavilionModeEnabled } from '../managers/notifications';
import { notifyPavilionModeDisabled } from '../managers/notifications';
import { USE_MESSAGE_ROUTER } from '../../../config/featureFlags';

const state = getStateStore('ipc');

const CHANGE_THRESHOLD = 0.2;

// Keep track of the last sent state to compare with new states
let lastSentState: HostMatchmakingState | null = null;
let lastSentTimestamp = 0;

// Periodic update interval (send update every 30 seconds even if nothing changed)
// This acts as a heartbeat to maintain presence in the matchmaking pool
const PERIODIC_UPDATE_INTERVAL_MS = 30000; // 30 seconds

/**
 * Determines if a numeric value has changed significantly (by more than 20%)
 */
const hasSignificantNumericChange = (oldValue: number, newValue: number): boolean => {
  if (oldValue === 0 && newValue !== 0) return true;
  if (oldValue === 0) return false;

  // Calculate the percent change
  const percentChange = Math.abs((newValue - oldValue) / oldValue);
  return percentChange > CHANGE_THRESHOLD;
};

/**
 * Determines if two host matchmaking states have significant differences
 * that warrant sending an update to the server
 */
const hasSignificantChanges = (oldState: HostMatchmakingState | null, newState: HostMatchmakingState): boolean => {
  // If we don't have a previous state, this is the first update
  if (!oldState) return true;

  // Check boolean values (any change is significant)
  if (oldState.globalMatchmakingToggle !== newState.globalMatchmakingToggle) return true;
  if (oldState.matchmakeable !== newState.matchmakeable) return true;

  // Check campaign ID (any change is significant - host switching games)
  if (oldState.campaignId !== newState.campaignId) return true;

  // Check game state (any change is significant - idle/prewarming/ready/active lifecycle)
  if (oldState.gameState !== newState.gameState) return true;

  // Check host type (any change is significant - cloud vs wild)
  if (oldState.isCloudHost !== newState.isCloudHost) return true;

  // Check bandwidth values (must change by more than 20%)
  const oldBandwidth = oldState.bandwidth;
  const newBandwidth = newState.bandwidth;

  if (hasSignificantNumericChange(oldBandwidth.uploadMbps, newBandwidth.uploadMbps)) return true;
  if (hasSignificantNumericChange(oldBandwidth.downloadMbps, newBandwidth.downloadMbps)) return true;

  // No significant changes detected
  return false;
};

export const initializeHostMatchmakingEvents = () => {
  let lastMatchmakeable = false;

  state.hostMatchmakingState.addListener((hostMatchmakerState) => {
    if (!steamInstallState.currentState.steamInstall) {
      return;
    }
    if (hostState.currentState.edition !== 'pavilion') {
      return;
    }

    const now = Date.now();
    const timeSinceLastUpdate = now - lastSentTimestamp;
    const hasBeenTooLong = timeSinceLastUpdate >= PERIODIC_UPDATE_INTERVAL_MS;

    // CRITICAL FIX: Always send update if matchmakeable/globalToggle changed
    // OR if it's been too long since last update (heartbeat)
    // OR if there are other significant changes
    const matchmakeableChanged = lastSentState?.matchmakeable !== hostMatchmakerState.matchmakeable;
    const globalToggleChanged = lastSentState?.globalMatchmakingToggle !== hostMatchmakerState.globalMatchmakingToggle;
    const criticalChangeDetected = matchmakeableChanged || globalToggleChanged;
    const otherSignificantChanges = hasSignificantChanges(lastSentState, hostMatchmakerState);

    const shouldSendUpdate = criticalChangeDetected || hasBeenTooLong || otherSignificantChanges;

    if (shouldSendUpdate) {
      console.log(`[HostMatchmakingEvents] Sending update - critical:${criticalChangeDetected}, heartbeat:${hasBeenTooLong}, other:${otherSignificantChanges}`);

      // Update the last sent state and timestamp
      lastSentState = { ...hostMatchmakerState };
      lastSentTimestamp = now;

      // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - remove when PavilionCoordinator fully owns this
      if (!USE_MESSAGE_ROUTER) {
        sendMatchmakerHostConfig().catch((err) => {
          console.error('[Matchmaker] Failed to send host config (state change):', err);
        });
      }
      // Check if matchmakeable status has changed
      if (lastMatchmakeable !== hostMatchmakerState.matchmakeable) {
        if (hostMatchmakerState.matchmakeable) {
          // Matchmaking was turned ON
          notifyPavilionModeEnabled();
        } else {
          // Matchmaking was turned OFF
          notifyPavilionModeDisabled();
        }
      }

      // // Check if global matchmaking toggle has changed
      // if (lastGlobalToggle !== hostMatchmakerState.globalMatchmakingToggle) {
      //   if (hostMatchmakerState.globalMatchmakingToggle) {
      //     // Global toggle was turned ON
      //     notifyPavilionModeEnabled();
      //   } else {
      //     // Global toggle was turned OFF
      //     notifyPavilionModeDisabled();
      //   }
      // }

      // Update last status values
      lastMatchmakeable = hostMatchmakerState.matchmakeable;
    }
  });
};
